import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaTrash, FaPhone, FaEnvelope, FaGlobe, FaMapMarkerAlt, FaCertificate, FaCheck, FaTimes, FaEdit, FaCamera } from 'react-icons/fa';

// Generate initials from company name
function getInitials(companyName) {
  if (!companyName) return '??';
  const words = companyName.trim().split(/\s+/);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return (words[0][0] + words[1][0]).toUpperCase();
}

// Company logo component with initials fallback
function CompanyLogo({ logo, companyName, size = 'md', API }) {
  const [imageError, setImageError] = useState(false);
  const sizeClasses = {
    sm: 'w-12 h-12 text-sm',
    md: 'w-16 h-16 text-lg',
    lg: 'w-24 h-24 text-2xl'
  };
  
  const initials = getInitials(companyName);
  
  if (logo && !imageError) {
    // Build the logo URL - handle different cases
    let logoUrl = logo;
    if (logo.startsWith('http')) {
      logoUrl = logo;
    } else if (logo.startsWith('/api/')) {
      const baseUrl = API.replace(/\/api$/, '');
      logoUrl = `${baseUrl}${logo}`;
    } else if (logo.startsWith('/')) {
      logoUrl = `${API}${logo}`;
    } else {
      logoUrl = `${API}/${logo}`;
    }
    
    return (
      <img 
        src={logoUrl} 
        alt={companyName}
        className={`${sizeClasses[size]} object-contain rounded-lg bg-white border p-1`}
        onError={() => setImageError(true)}
      />
    );
  }
  
  return (
    <div className={`${sizeClasses[size]} rounded-lg bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] flex items-center justify-center text-white font-bold`}>
      {initials}
    </div>
  );
}

export default function DirectoryManagement() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editListing, setEditListing] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    category: '',
    categories: [],
    description_fr: '',
    description_en: '',
    country: '',
    city: '',
    address: '',
    postal_code: '',
    phone: '',
    email: '',
    website: '',
    logo: '',
    services: [],
    certifications: [],
    company_size: '',
    owner_id: ''
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchListings();
      fetchCategories();
      fetchCountries();
      fetchSuppliers();  // Charger les fournisseurs
    }
  }, [user, filter]);

  const fetchListings = async () => {
    try {
      setLoading(true);
      let url = `${API}/directory/admin/all?admin_id=${user.id}`;
      if (filter !== 'all') {
        url += `&status=${filter}`;
      }
      const response = await axios.get(url);
      setListings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Erreur lors du chargement');
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      // Fetch new hierarchical categories
      const response = await axios.get(`${API}/directory/families`);
      const families = response.data;
      
      // Flatten categories from all families for the select dropdown
      const allCategories = [];
      for (const family of families) {
        // Fetch categories for each family
        try {
          const familyResponse = await axios.get(`${API}/directory/families/${family.slug}`);
          if (familyResponse.data.categories) {
            for (const cat of familyResponse.data.categories) {
              allCategories.push({
                id: cat.slug,
                name_fr: `${family.name_fr} > ${cat.name_fr}`,
                family_slug: family.slug
              });
            }
          }
        } catch (e) {
          console.error(`Error fetching family ${family.slug}:`, e);
        }
      }
      setCategories(allCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/directory/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchSuppliers = async () => {
    try {
      // Récupérer tous les users avec le rôle supplier
      const response = await axios.get(`${API}/users`);
      const supplierUsers = response.data.filter(u => 
        u.roles && u.roles.includes('supplier')
      );
      setSuppliers(supplierUsers);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
    }
  };

  const handleApprove = async (listingId) => {
    try {
      await axios.put(`${API}/directory/admin/approve/${listingId}?admin_id=${user.id}`);
      toast.success('Fiche approuvée !');
      fetchListings();
    } catch (error) {
      console.error('Error approving listing:', error);
      toast.error('Erreur lors de l\'approbation');
    }
  };

  const handleReject = async (listingId) => {
    try {
      await axios.put(`${API}/directory/admin/reject/${listingId}?admin_id=${user.id}`);
      toast.success('Fiche rejetée');
      fetchListings();
    } catch (error) {
      console.error('Error rejecting listing:', error);
      toast.error('Erreur lors du rejet');
    }
  };

  const handleDelete = async (listingId, companyName) => {
    if (!confirm(`Supprimer définitivement "${companyName}" ?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/directory/admin/delete/${listingId}?admin_id=${user.id}`);
      toast.success('Fiche supprimée');
      fetchListings();
    } catch (error) {
      console.error('Error deleting listing:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (listing) => {
    setEditListing(listing);
    // Initialize categories array from both single category and categories array
    let categoriesArray = listing.categories || [];
    if (listing.category && !categoriesArray.includes(listing.category)) {
      categoriesArray = [listing.category, ...categoriesArray];
    }
    
    setFormData({
      company_name: listing.company_name,
      category: listing.category,
      categories: categoriesArray,  // NEW: Multiple categories
      description_fr: listing.description_fr,
      description_en: listing.description_en || '',
      country: listing.country,
      city: listing.city,
      address: listing.address || '',
      postal_code: listing.postal_code || '',
      phone: listing.phone,
      email: listing.email,
      website: listing.website || '',
      logo: listing.logo || '',
      services: listing.services || [],
      certifications: listing.certifications || [],
      company_size: listing.company_size || '',
      owner_id: listing.owner_id || ''  // Propriétaire (fournisseur)
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();

    try {
      await axios.put(
        `${API}/directory/admin/update/${editListing.id}?admin_id=${user.id}`,
        formData
      );
      toast.success('Fiche mise à jour');
      setEditDialogOpen(false);
      fetchListings();
    } catch (error) {
      console.error('Error updating listing:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleCertification = (cert) => {
    setFormData(prev => ({
      ...prev,
      certifications: prev.certifications.includes(cert)
        ? prev.certifications.filter(c => c !== cert)
        : [...prev.certifications, cert]
    }));
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 5MB)');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Le fichier doit être une image');
      return;
    }

    setUploadingLogo(true);
    try {
      const uploadData = new FormData();
      uploadData.append('file', file);
      const response = await axios.post(`${API}/directory/upload-logo`, uploadData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, logo: response.data.logo_url }));
      toast.success('Logo uploadé avec succès');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Erreur lors de l\'upload du logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Remove logo
  const handleRemoveLogo = () => {
    setFormData(prev => ({ ...prev, logo: '' }));
    toast.success('Logo supprimé');
  };

  // NEW: Toggle category in multiple categories array
  const toggleCategory = (categoryId) => {
    setFormData(prev => {
      const currentCategories = prev.categories || [];
      const newCategories = currentCategories.includes(categoryId)
        ? currentCategories.filter(c => c !== categoryId)
        : [...currentCategories, categoryId];
      
      return {
        ...prev,
        categories: newCategories,
        // Keep primary category as first selected, for backward compatibility
        category: newCategories.length > 0 ? newCategories[0] : ''
      };
    });
  };

  const handlePositionChange = async (listingId, newPosition) => {
    try {
      await axios.patch(`${API}/directory/admin/${listingId}/position?position=${newPosition}`);
      
      // Update local state
      setListings(prevListings => 
        prevListings.map(l => 
          l.id === listingId ? { ...l, position: newPosition } : l
        ).sort((a, b) => (a.position || 999) - (b.position || 999))
      );
      
      toast.success(`Position mise a jour: ${newPosition}`);
    } catch (error) {
      console.error('Error updating position:', error);
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { text: 'En attente', class: 'bg-yellow-100 text-yellow-800' },
      approved: { text: 'Approuvé', class: 'bg-[#635BFF]/10 text-[#5248d9]' },
      rejected: { text: 'Rejeté', class: 'bg-red-100 text-red-800' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge.class}`}>
        {badge.text}
      </span>
    );
  };

  const stats = {
    all: listings.length,
    pending: listings.filter(l => l.status === 'pending').length,
    approved: listings.filter(l => l.status === 'approved').length,
    rejected: listings.filter(l => l.status === 'rejected').length
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p>Chargement...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[#0A2540] mb-2">
            Gestion de l'Annuaire
          </h1>
          <p className="text-gray-600">
            Gérez toutes les fiches professionnelles
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('all')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Total</p>
              <p className="text-2xl font-bold text-[#0A2540]">{stats.all}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('pending')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">En attente</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('approved')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Approuvés</p>
              <p className="text-2xl font-bold text-[#635BFF]">{stats.approved}</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:shadow-lg transition" onClick={() => setFilter('rejected')}>
            <CardContent className="p-4">
              <p className="text-sm text-gray-600">Rejetés</p>
              <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        <div className="mb-6">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-full md:w-64">
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous ({stats.all})</SelectItem>
              <SelectItem value="pending">En attente ({stats.pending})</SelectItem>
              <SelectItem value="approved">Approuvés ({stats.approved})</SelectItem>
              <SelectItem value="rejected">Rejetés ({stats.rejected})</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Listings */}
        {listings.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              Aucune fiche
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {listings.map(listing => (
              <Card key={listing.id} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    {/* Position */}
                    <div className="flex-shrink-0 flex items-center">
                      <div className="text-center">
                        <label className="text-xs text-gray-500 block mb-1">Pos.</label>
                        <input
                          type="number"
                          min="1"
                          value={listing.position || 999}
                          onChange={(e) => handlePositionChange(listing.id, parseInt(e.target.value) || 999)}
                          className="w-14 h-8 text-center text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[#635BFF]"
                        />
                      </div>
                    </div>

                    {/* Logo avec initiales par défaut */}
                    <div className="flex-shrink-0">
                      <CompanyLogo 
                        logo={listing.logo} 
                        companyName={listing.company_name} 
                        size="lg" 
                        API={API} 
                      />
                    </div>

                    {/* Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-center gap-3">
                        <h3 className="text-xl font-bold text-[#0A2540]">
                          {listing.company_name}
                        </h3>
                        {getStatusBadge(listing.status)}
                      </div>

                      <div className="flex flex-wrap gap-2 items-center text-sm">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                          {categories.find(c => c.id === listing.category)?.[i18n.language === 'fr' ? 'name_fr' : 'name_en']}
                        </span>
                        <span className="text-gray-600">
                          <FaMapMarkerAlt className="inline mr-1" />
                          {listing.city}, {listing.country}
                        </span>
                      </div>

                      <p className="text-sm text-gray-700 line-clamp-2">
                        {listing.description_fr}
                      </p>

                      <div className="flex flex-wrap gap-4 text-sm">
                        <a href={`tel:${listing.phone}`} className="flex items-center hover:text-[#635BFF]">
                          <FaPhone className="mr-2" />
                          {listing.phone}
                        </a>
                        <a href={`mailto:${listing.email}`} className="flex items-center hover:text-[#635BFF]">
                          <FaEnvelope className="mr-2" />
                          {listing.email}
                        </a>
                      </div>

                      <div className="text-xs text-gray-500">
                        Créé le: {new Date(listing.created_at).toLocaleDateString('fr-FR')}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex md:flex-col gap-2 md:min-w-[140px]">
                      {listing.status === 'pending' && (
                        <>
                          <Button
                            onClick={() => handleApprove(listing.id)}
                            size="sm"
                            className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]"
                          >
                            <FaCheck className="mr-2" />
                            Approuver
                          </Button>
                          <Button
                            onClick={() => handleReject(listing.id)}
                            size="sm"
                            variant="outline"
                            className="flex-1"
                          >
                            <FaTimes className="mr-2" />
                            Rejeter
                          </Button>
                        </>
                      )}
                      {listing.status === 'rejected' && (
                        <Button
                          onClick={() => handleApprove(listing.id)}
                          size="sm"
                          className="flex-1 bg-[#635BFF] hover:bg-[#5248d9]"
                        >
                          <FaCheck className="mr-2" />
                          Approuver
                        </Button>
                      )}
                      <Button
                        onClick={() => handleEdit(listing)}
                        size="sm"
                        variant="outline"
                        className="flex-1"
                      >
                        <FaEdit className="mr-2" />
                        Modifier
                      </Button>
                      <Button
                        onClick={() => handleDelete(listing.id, listing.company_name)}
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                      >
                        <FaTrash className="mr-2" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier la fiche</DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nom de l'entreprise *</Label>
                  <Input
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                  />
                </div>

                {/* Logo Section */}
                <div>
                  <Label>Logo de l'entreprise</Label>
                  <div className="mt-2 flex items-start gap-4">
                    {/* Current logo or initials */}
                    <div className="flex-shrink-0">
                      <CompanyLogo 
                        logo={formData.logo} 
                        companyName={formData.company_name} 
                        size="lg" 
                        API={API} 
                      />
                    </div>
                    
                    {/* Upload controls */}
                    <div className="flex-1 space-y-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                        className="text-sm"
                      />
                      {uploadingLogo && (
                        <p className="text-xs text-[#635BFF]">Upload en cours...</p>
                      )}
                      {formData.logo && (
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={handleRemoveLogo}
                          className="text-red-600 hover:text-red-700"
                        >
                          <FaTrash className="mr-1 w-3 h-3" />
                          Supprimer le logo
                        </Button>
                      )}
                      <p className="text-xs text-gray-500">
                        JPG, PNG, WebP (max 5MB). Sans logo, les initiales seront affichées.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label>Catégories * (sélectionnez une ou plusieurs)</Label>
                  <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg p-3 bg-gray-50">
                    {categories.length === 0 ? (
                      <p className="text-gray-500 text-sm">Chargement des catégories...</p>
                    ) : (
                      <div className="space-y-2">
                        {categories.map(cat => (
                          <div key={cat.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`cat-${cat.id}`}
                              checked={(formData.categories || []).includes(cat.id)}
                              onCheckedChange={() => toggleCategory(cat.id)}
                            />
                            <label 
                              htmlFor={`cat-${cat.id}`}
                              className="text-sm cursor-pointer hover:text-[#635BFF]"
                            >
                              {cat.name_fr}
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(formData.categories || []).length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formData.categories.length} catégorie(s) sélectionnée(s)
                    </p>
                  )}
                </div>

                <div>
                  <Label>Propriétaire (Fournisseur)</Label>
                  <Select value={formData.owner_id || "none"} onValueChange={(value) => setFormData({...formData, owner_id: value === "none" ? "" : value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un fournisseur" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun propriétaire</SelectItem>
                      {suppliers.map(supplier => (
                        <SelectItem key={supplier.id} value={supplier.id}>
                          {supplier.name} ({supplier.email})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Pays *</Label>
                  <Select value={formData.country || "none"} onValueChange={(value) => setFormData({...formData, country: value === "none" ? "" : value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un pays" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Sélectionner --</SelectItem>
                      {countries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Ville *</Label>
                  <Input
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Téléphone *</Label>
                  <Input
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>

                <div>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Site web</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({...formData, website: e.target.value})}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Description (FR) *</Label>
                  <textarea
                    required
                    rows={3}
                    value={formData.description_fr}
                    onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Description (EN)</Label>
                  <textarea
                    rows={3}
                    value={formData.description_en}
                    onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Services</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    {['vente', 'location', 'maintenance', 'formation'].map(service => (
                      <div key={service} className="flex items-center space-x-2">
                        <Checkbox
                          checked={formData.services.includes(service)}
                          onCheckedChange={() => toggleService(service)}
                        />
                        <Label className="cursor-pointer capitalize">{service}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label>Certifications</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-2">
                    {['ISO', 'CE', 'NF', 'Autre'].map(cert => (
                      <div key={cert} className="flex items-center space-x-2">
                        <Checkbox
                          checked={formData.certifications.includes(cert)}
                          onCheckedChange={() => toggleCertification(cert)}
                        />
                        <Label className="cursor-pointer">{cert}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#635BFF] hover:bg-[#5248d9]">
                  Enregistrer
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
